// @givmo/api-client — paginated-envelope unwrap + pagination params.
//
// Ports the Dio `ResponseInterceptor` (givmo-webapp): the backend wraps
// paginated list payloads as `{ data, meta, links }` and returns singletons
// raw. The unwrap is SHAPE-GATED on the pagination markers (`meta` OR `links`
// alongside `data`) so a future singleton endpoint whose payload happens to have
// a top-level `data` field is not silently stripped.

/** True iff `body` is the `{ data, meta|links }` paginated envelope. */
export function isPaginatedEnvelope(body: unknown): body is {
  data: unknown;
  meta?: unknown;
  links?: unknown;
} {
  if (typeof body !== 'object' || body === null) return false;
  const obj = body as Record<string, unknown>;
  return 'data' in obj && ('meta' in obj || 'links' in obj);
}

/**
 * Unwrap a decoded body: returns `body.data` when it matches the paginated
 * envelope shape, otherwise the body unchanged. The `meta`/`links` are dropped
 * from the typed `data` the SDK returns — callers that need them read the raw
 * response (binary/pagination helpers) instead.
 */
export function unwrapEnvelope(body: unknown): unknown {
  return isPaginatedEnvelope(body) ? body.data : body;
}

/** The backend pagination query contract: `page[limit]` / `page[offset]`. */
export interface PageParams {
  readonly limit?: number;
  readonly offset?: number;
}

/**
 * Build the `page[limit]` / `page[offset]` query object. The generated client's
 * deep-object query serializer emits these as bracketed keys when nested under a
 * `page` key, but the backend expects the literal bracketed names, so we emit
 * the flat bracket-keyed object directly.
 */
export function pageQuery(params: PageParams): Record<string, number> {
  const query: Record<string, number> = {};
  if (params.limit !== undefined) query['page[limit]'] = params.limit;
  if (params.offset !== undefined) query['page[offset]'] = params.offset;
  return query;
}
